import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, Mail, AlertTriangle, CheckCircle2, CalendarClock, History, Sparkles, Eye } from "lucide-react";

const LEVEL_LABELS = { 1: "Rappel courtois", 2: "Relance ferme", 3: "Mise en demeure" };
const LEVEL_COLORS = { 1: "#38bdf8", 2: "#f59e0b", 3: "#f43f5e" };

export const RelancesIADialog = ({ open, onClose, api, onSent }) => {
  const [overdue, setOverdue] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [result, setResult] = useState(null);
  const [creatingProcess, setCreatingProcess] = useState(false);
  const [processMsg, setProcessMsg] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const [o, h] = await Promise.all([
        api.get("/facturation/relances/overdue"),
        api.get("/facturation/relances/ai-history"),
      ]);
      setOverdue(o.data);
      setHistory(h.data.history || []);
    } catch (e) { /* silencieux */ }
  };

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setProcessMsg("");
    setExpanded(null);
    load();
    // eslint-disable-next-line
  }, [open]);

  const run = async (dryRun) => {
    dryRun ? setPreviewing(true) : setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/facturation/relances/send-ai", { dry_run: dryRun });
      setResult(r.data);
      if (!dryRun && r.data.sent > 0) { onSent?.(); load(); }
    } catch (e) {
      setResult({ sent: 0, errors: 1, skipped: 0, details: [], error_msg: e.response?.data?.detail || "Erreur" });
    } finally {
      setRunning(false);
      setPreviewing(false);
    }
  };

  const createDailyProcess = async () => {
    setCreatingProcess(true);
    setProcessMsg("");
    try {
      await api.post("/ai/processes", {
        name: "Relances quotidiennes des impayés",
        agent_key: "comptabilite",
        schedule_type: "daily",
        schedule_time: "09:00",
        allow_actions: true,
        notify: true,
        instructions:
          "Chaque matin, vérifie les factures clients en retard de paiement et envoie les relances adaptées (rappel courtois, relance ferme, mise en demeure selon le niveau) en déclenchant l'action send_invoice_reminders. Termine par un compte-rendu : relances envoyées, montants concernés, clients à surveiller.",
      });
      setProcessMsg("Process quotidien créé (Agent Comptabilité, 09:00) — visible dans la page Agent IA.");
    } catch (e) {
      setProcessMsg(e.response?.data?.detail || "Erreur lors de la création du process");
    } finally {
      setCreatingProcess(false);
    }
  };

  const eligibleCount = overdue?.invoices?.filter(i => i.eligible).length || 0;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="relances-ia-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#f7c935]" /> Relances IA — factures en retard
          </DialogTitle>
        </DialogHeader>

        {/* Récap impayés */}
        <div className="rounded-lg border border-white/10 p-3" data-testid="relances-overdue-summary">
          {overdue === null ? (
            <span className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</span>
          ) : overdue.invoices.length === 0 ? (
            <span className="text-emerald-400 text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Aucune facture en retard — tout est à jour !</span>
          ) : (
            <>
              <p className="text-sm text-white font-bold mb-2">
                {overdue.invoices.length} facture(s) en retard · {overdue.total_remaining.toLocaleString("fr-FR")} EUR à récupérer · {eligibleCount} éligible(s) à une relance
              </p>
              <div className="space-y-1 max-h-44 overflow-y-auto">
                {overdue.invoices.map((i) => (
                  <div key={i.invoice_id} className="flex items-center gap-2 text-xs py-1 border-t border-white/5" data-testid={`overdue-${i.invoice_id}`}>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ color: LEVEL_COLORS[i.next_level], background: `${LEVEL_COLORS[i.next_level]}20` }}>
                      Niv. {i.next_level}
                    </span>
                    <span className="text-white font-medium">{i.reference}</span>
                    <span className="text-slate-400">{i.client_name}</span>
                    <span className="text-slate-500">· {i.days_overdue}j de retard</span>
                    <span className="ml-auto text-[#f7c935] font-mono">{i.remaining.toLocaleString("fr-FR")} EUR</span>
                    {!i.eligible && <span className="text-slate-600 text-[9px]">(relancée récemment ou sans email)</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => run(true)} disabled={previewing || running || !eligibleCount} variant="outline"
            className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="relances-preview-btn">
            {previewing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Eye className="w-4 h-4 mr-1.5" />}
            Prévisualiser les emails IA
          </Button>
          <Button onClick={() => run(false)} disabled={running || previewing || !eligibleCount}
            className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e5b820] font-bold" data-testid="relances-send-btn">
            {running ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Mail className="w-4 h-4 mr-1.5" />}
            {running ? "Envoi en cours…" : `Envoyer les relances (${eligibleCount})`}
          </Button>
          <Button onClick={createDailyProcess} disabled={creatingProcess} variant="outline"
            className="border-white/10 text-slate-300 hover:bg-white/5" data-testid="relances-create-process-btn">
            {creatingProcess ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarClock className="w-4 h-4 mr-1.5" />}
            Automatiser chaque matin (09:00)
          </Button>
        </div>
        {processMsg && <p className="text-xs text-[#f7c935]" data-testid="relances-process-msg">{processMsg}</p>}

        {/* Résultats / prévisualisation */}
        {result && (
          <div className="rounded-lg border border-white/10 p-3 space-y-2" data-testid="relances-results">
            <p className="text-sm font-bold text-white">
              {result.dry_run ? "Prévisualisation — aucun email envoyé" : `${result.sent} relance(s) envoyée(s)`}
              {result.errors > 0 && <span className="text-rose-400"> · {result.errors} erreur(s)</span>}
              {result.skipped > 0 && <span className="text-slate-500"> · {result.skipped} ignorée(s)</span>}
            </p>
            {result.error_msg && <p className="text-xs text-rose-400">{result.error_msg}</p>}
            {(result.details || []).map((d) => (
              <div key={d.invoice_id} className="border-t border-white/5 py-1.5" data-testid={`relance-detail-${d.invoice_id}`}>
                <button onClick={() => setExpanded(expanded === d.invoice_id ? null : d.invoice_id)} className="w-full flex items-center gap-2 text-xs text-left">
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ color: LEVEL_COLORS[d.level] || "#94a3b8", background: `${LEVEL_COLORS[d.level] || "#94a3b8"}20` }}>
                    {LEVEL_LABELS[d.level] || `Niv. ${d.level}`}
                  </span>
                  <span className="text-white">{d.reference} — {d.client_name}</span>
                  {d.status === "sent" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                  {d.status === "error" && <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />}
                  <span className="ml-auto text-slate-500">{expanded === d.invoice_id ? "Réduire" : "Voir l'email"}</span>
                </button>
                {d.error && <p className="text-[10px] text-rose-400 mt-0.5">{d.error}</p>}
                {expanded === d.invoice_id && (
                  <div className="mt-1.5 bg-[#0b0811] border border-white/5 rounded-lg p-2.5 text-xs">
                    <p className="text-[#f7c935] font-bold mb-1">{d.subject}</p>
                    <p className="text-slate-300 whitespace-pre-wrap">{d.body}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Historique */}
        {history.length > 0 && (
          <div className="rounded-lg border border-white/10 p-3" data-testid="relances-history">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2">
              <History className="w-3.5 h-3.5" /> Dernières relances IA
            </p>
            {history.slice(0, 8).map((h) => (
              <div key={h.relance_id} className="flex items-center gap-2 text-xs text-slate-400 py-1 border-t border-white/5">
                <span>{new Date(h.sent_at).toLocaleDateString("fr-FR")}</span>
                <span className="text-white">{h.reference}</span>
                <span>{h.client_name}</span>
                <span className="px-1.5 rounded text-[9px] font-bold" style={{ color: LEVEL_COLORS[h.level] || "#94a3b8" }}>Niv. {h.level}</span>
                <span className="ml-auto">{h.email_sent ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />}</span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RelancesIADialog;
